import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ViewChild, ViewEncapsulation, computed, effect, input, model, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { simpleDeepClone } from '@phx-erp/shared';
import { TreeDragDropService, TreeNode } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { MessageModule } from 'primeng/message';
import { SplitterModule } from 'primeng/splitter';
import { Table, TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { lastValueFrom } from 'rxjs';
import { DecimalPlacesCustomPipe } from '../../../../@core/pipes';
import { ActionBarItemsComponent } from '../../../../shared/components/action-bar-items/action-bar-items.component';
import { v4 as uuidv4 } from 'uuid';
import { I18nService } from '../../../../@core/services/i18n/i18n.service';
import { ModalService } from '../../../../@core/services/modal/modal.service';
import { NotificationService } from '../../../../@core/services/notification/notification.service';
import { SettingsService } from '../../../../@core/services/settings.service';
import { Product, ProductType, TradingSetPriceInfo, TradingSetRelation, TradingSetRelationInput } from '../../../../generated/generated-types';
import { PhxInputNumberComponent } from '../../../../shared/components/phx-input-number/phx-input-number.component';
import { ProductService } from '../../services/product.service';
import { TradingSetRelationService } from '../../services/trading-set-relation.service';
import { PriceListDisplayRowWidgetComponent } from '../price-list-display-row-widget/price-list-display-row-widget.component';
import { WidgetProductListComponent } from '../product-list-widget/product-list-widget.component';

/**
 * @description this component doesnt use newBaseList since it will in future be used with multiple dimensions - and its easier to customize with separate pTable
 * @warning dont use pTreeTable since it doesnt allow rowEdit (Feature request since years and nothing done from primeng) and so our consistency would be lost
 */
@Component({
  selector: 'app-product-trading-set-configurator-widget',
  templateUrl: './product-trading-set-configurator-widget.component.html',
  styleUrl: './product-trading-set-configurator-widget.component.scss',
  encapsulation: ViewEncapsulation.Emulated,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [TreeDragDropService],
  imports: [
    CommonModule,
    SplitterModule,
    ButtonModule,
    TableModule,
    CardModule,
    IconFieldModule,
    PhxInputNumberComponent,
    InputIconModule,
    FormsModule,
    MessageModule,
    PriceListDisplayRowWidgetComponent,
    ActionBarItemsComponent,
    TranslatePipe,
    DecimalPlacesCustomPipe,
    TooltipModule,
  ]
})
export class ProductTradingSetConfiguratorWidgetComponent {

  productId = input.required<string>();

  dataNodes = signal<TreeNode[]>([]);

  dataTestid = input<string>("product-trading-set-configurator-widget");

  priceInfos = signal<TradingSetPriceInfo[]>([]);

  @ViewChild(Table) table: Table;

  /**
 * @description this are not saved new products for trading set
 */
  newNodes = signal<TreeNode[]>([]);

  loading = signal(false);

  editProductFeature = signal(false);

  selectedNode: TreeNode; //todo signal 
  editNode = signal<TreeNode>(null);
  originalNode = signal<TreeNode>(null);
  /**
   * those are all the sub relations of the main product
   */
  activeTradingSets = signal<TradingSetRelation[]>([]);

  masterTradingSet = signal<TradingSetRelation>(null);

  productSidebarVisible = signal(false);

  productsFromSearch = signal<Product[]>([]);
  selectedTradingSet = signal<TradingSetRelation>(null);
  decimalPlaces = model<number>()

  tradingSetCalcPriceSig = computed(() => {
    return this.dataNodes().reduce((acc, node) => {
      return acc + (node.data as TradingSetRelation).quantity * (node.data as TradingSetRelation).childProduct?.calculatoryCosts;
    }, 0);
  });

  tradingSetLastPurchasePriceSig = computed(() => {
    return this.dataNodes().reduce((acc, node) => {
      return acc + (node.data as TradingSetRelation).quantity * (node.data as TradingSetRelation).childProduct?.lastPurchasePrice;
    }, 0);
  });

  tradingSetAveragePurchasePriceSig = computed(() => {
    return this.dataNodes().reduce((acc, node) => {
      return acc + (node.data as TradingSetRelation).quantity * (node.data as TradingSetRelation).childProduct?.averagePurchasePrice;
    }, 0);
  });

  constructor(
    private productService: ProductService,
    private notificationService: NotificationService,
    private i18nService: I18nService,
    private modalService: ModalService,
    private settingsService: SettingsService,
    private cdRef: ChangeDetectorRef,
    private tradingSetService: TradingSetRelationService,
  ) {
    effect(async () => {
      if (!this.productId()) {
        this.dataNodes.set([]);
        this.cdRef.markForCheck();
        return;
      }
      untracked(async () => {
        let res = await this.loadTradingSetOfProduct(this.productId());
        //set base data
        this.dataNodes.set(this.tradingSetsToTreeNodes(res));
      })

    });

    effect(async () => {
      if (this.dataNodes()?.length > 0)
        untracked(async () => await this.reloadPriceInfos());
    })

  }

  ngOnInit() {
    this.decimalPlaces.set(this.settingsService.getSetting("S_TENANT_DOCUMENT_LINE_ITEM_ROUND_DECIMAL_PLACES"));
  }

  addNewProduct(node?: TreeNode) {
    let sub = this.modalService.fromComponent(WidgetProductListComponent, {
      title: this.i18nService.translate('product.add-product'),
      size: "lg",
      closable: true,
      maximizable: true,
      height: '80vh',
      locals: {
        checkboxMode: true,
        noAddNew: true,
        noDelete: true,
        noBaseEdit: true,
        noCopy: true,
        noPrinter: true
      },
      setLocalsSig: {
        withoutProductTypeSig: ProductType.TRADINGSET
      }
    }).subscribe(async (result) => {

      if (result)
        for (let product of result) {

          let newTS: TradingSetRelation = {
            childProduct: product,
            childProductId: product.id,
            quantity: 1, //TODO based on some defaults

          } as TradingSetRelation;

          //avoid self relations
          if (newTS.childProductId == this.productId())
            continue;

          // tn.label += ' ' + tn.key;

          //this dynamic sub tree should not be supported for now
          // if (this.selectedNode) {
          //   let ts = this.selectedNode.data as TradingSetRelation;
          //   newTS.parentProductId = ts.parentProductId;
          //   // newTS.childProductId = product.id;
          //   // newTS.childProduct = product;

          //   if (!this.selectedNode.children)
          //     this.selectedNode.children = [];

          //   let tn = this.tradingSetToTreeNode(newTS);

          //   this.selectedNode.children.push(tn);
          //   this.cdRef.markForCheck();
          // }
          // else {
          newTS.parentProductId = this.productId();

          let tn = this.tradingSetToTreeNode(newTS);

          let selected = node ?? this.selectedNode;
          // insert after node
          if (selected) {
            let nodeIndex = this.dataNodes().findIndex(x => x.key === selected.key);
            this.dataNodes().splice(nodeIndex + 1, 0, tn);
            this.dataNodes.set([...this.dataNodes()]);
          }
          else {
            this.dataNodes.set([...this.dataNodes(), tn]);
          }
          this.newNodes.set([...this.newNodes(), tn]);
          // }
        }
      await this.save(this.newNodes());

      sub.unsubscribe();
    });
  }

  async loadTradingSetOfProduct(productId: string) {
    let res = await lastValueFrom(await this.tradingSetService.getData({
      parentProductId: productId,
      take: 1000 //hard limit for now
    }));

    return simpleDeepClone(res.result) as TradingSetRelation[];
  }

  tradingSetsToTreeNodes(tradingSets: TradingSetRelation[]): TreeNode[] {
    let res: TreeNode[] = [];
    tradingSets.forEach(ts => {
      let tn: TreeNode = this.tradingSetToTreeNode(ts);
      if (!ts.quantity)
        ts.quantity = 1;
      res.push(tn);
    });

    return res;
  }

  tradingSetToTreeNode(ts: TradingSetRelation): TreeNode {
    let tn: TreeNode = {
      label: `${ts.childProduct?.identifier} ${ts.childProduct?.description}`,
      data: ts,
      key: uuidv4(),
      droppable: false
    };

    switch (ts.childProduct.productType) {
      case ProductType.BASIC:
      case ProductType.STORE:
        tn.icon = 'fa-solid fa-cube';
        break;
      case ProductType.TRADINGSET:
        tn.icon = 'fa fa-cubes';
        break;
      case ProductType.SHIPPING:
        tn.icon = 'fa fa-truck';
        break;
    }

    // tn.label += ' ' + tn.key; //DEBUG

    return tn;
  }

  nodeToTradingSet(node: TreeNode) {
    return node.data as TradingSetRelation;
  }

  async nodeExpand(event: any) {
    // if (event.node) {
    //   this.loading.set(true);

    //   let res = await this.loadTradingSetOfProduct(event.node.data.parentProductId);
    //   event.node.children = this.tradingSetsToTreeNodes(res);

    //   this.loading.set(false);
    //   this.cdRef.markForCheck();
    // }
  }

  async save(selectedNodes?: TreeNode[]) {

    let allRelations: TradingSetRelationInput[] = [];

    let i = 1;
    //we loop thro all the top level nodes
    for (let node of this.dataNodes()) {

      if (selectedNodes && selectedNodes.length > 0 && !selectedNodes.find(x => x.key === node.key))
        continue;

      let ts = simpleDeepClone(node.data as TradingSetRelation);
      //dont delete childProduct since inline edit will not work
      delete ts.parentProduct;

      let tsInput: TradingSetRelationInput = {
        ...ts
      };
      tsInput.order = i;
      tsInput.parentProductId = this.productId(); // just for safety

      allRelations.push(tsInput);

      //this is for future
      // let subRelations = this.recursivelyGetAllTradingSetRelations(node);
      // if (subRelations.length > 0)
      //   allRelations.push(...subRelations);

      i++;
    }

    this.loading.set(true);
    try {
      if (selectedNodes && selectedNodes.length > 0) {
        for (let node of selectedNodes) {
          node.data = await this.tradingSetService.saveItem(node.data as TradingSetRelation);
          this.table.cancelRowEdit(node);
        }
      }
      //for saving all
      else {
        let nodes = (await this.tradingSetService.saveItems(allRelations))?.saveTradingSetRelations;
        this.dataNodes.set(this.tradingSetsToTreeNodes(nodes));
      }
      this.newNodes.set([]);
    }
    catch (e) {
      console.error(e);
    }
    this.editNode.set(null);
    this.table.editingRowKeys = {};
    // change detection for any cases - needed for effect as well
    this.dataNodes.set([...this.dataNodes()]);
    this.cdRef.markForCheck();

    this.loading.set(false);
  }

  recursivelyGetAllTradingSetRelations(node: TreeNode): TradingSetRelation[] {
    let returnVal: TradingSetRelation[] = [];

    if (node.children) {
      for (let i = 0; i < node.children.length; i++) {
        let child = node.children[i];
        let tsr = child.data as TradingSetRelation;
        tsr.order = i;
        returnVal.push(tsr);
        returnVal = returnVal.concat(this.recursivelyGetAllTradingSetRelations(child));
      }
    }

    return returnVal;
  }

  async reloadPriceInfos() {
    let priceInfos: TradingSetPriceInfo[] = simpleDeepClone(await this.tradingSetService.getTradingSetPriceInfoData(this.productId(), {}));
    this.priceInfos.set(priceInfos);
  }

  async deleteTradingSetNode(node: TreeNode) {
    this.loading.set(true);

    try {

      let ts = node.data as TradingSetRelation;
      if (ts.id) {
        await this.tradingSetService.deleteItem(ts.id);
      }
      if (this.selectedTradingSet() === ts) {
        this.selectedTradingSet.set(null);
      }
      if (this.selectedNode?.key === node.key) {
        this.selectedNode = null;
      }
      this.selectedTradingSet.set(null);
      this.dataNodes.set(this.dataNodes().filter(x => x.key !== node.key));
      this.newNodes.set(this.newNodes().filter(x => x.key !== node.key));
    }
    catch (e) {
      console.error(e);
    }
    this.loading.set(false);
  }

  async toggleSidebar() {
    this.productSidebarVisible.set(!this.productSidebarVisible());
    this.cdRef.markForCheck();
  }

  draggedProduct: Product | undefined | null;
  dragStart(product: Product) {
    this.draggedProduct = product;
  }

  dragEnd() {
    this.draggedProduct = null;
  }

  drop() {
    if (this.draggedProduct) {

      let newTradingSet: TradingSetRelation = {
        childProduct: this.draggedProduct,
        childProductId: this.draggedProduct.id,
        parentProductId: this.productId() //TODO find parent
      } as TradingSetRelation;

      let tn = this.tradingSetToTreeNode(newTradingSet);

      this.dataNodes.set([...this.dataNodes(), tn]);

      this.activeTradingSets.set([...this.activeTradingSets(), newTradingSet]);

      // let draggedProductIndex = this.findIndex(this.draggedProduct);
      // this.selectedProducts = [...(this.selectedProducts as Product[]), this.draggedProduct];
      // this.availableProducts = this.availableProducts?.filter((val, i) => i != draggedProductIndex);
      this.draggedProduct = null;
    }
  }

  selectedTradingSetChanged(event: any) {
    this.selectedTradingSet.set(event.value);
  }

  onRowEditInit(rowData: TreeNode, index?: number) {

    if (this.editNode() != null)
      return;

    this.originalNode.set(simpleDeepClone(rowData));
    this.editNode.set(rowData);
    this.table.initRowEdit(rowData);
    this.cdRef.markForCheck();
  }

  async onRowEditSave(selectedNode: TreeNode, index?: number) {
    await this.save([selectedNode]);
    this.cdRef.markForCheck();
  }

  onRowEditCancel(selectedNode: TreeNode, index?: number) {
    selectedNode.data = this.originalNode().data;
    this.originalNode.set(null);
    this.editNode.set(null);
    this.table.cancelRowEdit(selectedNode);
    this.cdRef.markForCheck();
  }

}
